'use client';

import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  HelpCircle, 
  BookOpen, 
  BarChart3, 
  TrendingUp, 
  Settings, 
  X,
  ChevronRight,
  PlayCircle,
  Zap,
  FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useStartTour } from './tour-manager';
import { useTour } from '@/lib/tour-context';
import { HelpCenter } from './help-center';
import { logger } from '@/lib/logger';
import { isHelpSystemEnabled } from '@/lib/feature-flags';
import { getAllTours, TourId } from '@/lib/tours';

interface HelpSidebarProps {
  className?: string;
}

export function HelpSidebar({ className }: HelpSidebarProps) {
  // Hooks must be called unconditionally - call them before any early returns
  const [isOpen, setIsOpen] = useState(false);
  const [helpCenterOpen, setHelpCenterOpen] = useState(false);
  const pathname = usePathname();
  const startTour = useStartTour();
  const { hasCompletedTour } = useTour();
  const [selectedArticleId, setSelectedArticleId] = useState<string | undefined>();

  // Don't show help on login page (user not authenticated)
  if (pathname === '/login') {
    return null;
  }

  // Check feature flag - return null if disabled (after hooks)
  if (!isHelpSystemEnabled()) {
    return null;
  }

  // Get all available tours - filter to only show tutorials (onboarding-* tours)
  // Exclude welcome tour (shown in getting started checklist)
  const allTours = getAllTours().filter(tour => tour.id.startsWith('onboarding-'));

  // Quick links - most helpful guides (no tutorials)
  const quickLinks = [
    {
      label: 'What is DT DebtCast?',
      icon: HelpCircle,
      articleId: 'what-is-debtcast',
      onClick: () => {
        logger.info('Help: What is DT DebtCast clicked');
        setSelectedArticleId('what-is-debtcast');
        setHelpCenterOpen(true);
      },
    },
    {
      label: 'Navigation Guide',
      icon: BookOpen,
      articleId: 'navigation-guide',
      onClick: () => {
        logger.info('Help: Navigation guide clicked');
        setSelectedArticleId('navigation-guide');
        setHelpCenterOpen(true);
      },
    },
    {
      label: 'Global Settings',
      icon: Settings,
      articleId: 'settings-guide',
      onClick: () => {
        logger.info('Help: Settings guide clicked');
        setSelectedArticleId('settings-guide');
        setHelpCenterOpen(true);
      },
    },
    {
      label: 'Using Dropdowns',
      icon: FileText,
      articleId: 'dropdowns-guide',
      onClick: () => {
        logger.info('Help: Dropdowns guide clicked');
        setSelectedArticleId('dropdowns-guide');
        setHelpCenterOpen(true);
      },
    },
  ];

  return (
    <>
      {/* Floating Help Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg',
          'bg-primary hover:bg-primary/90',
          'flex items-center justify-center',
          className
        )}
        aria-label="Open help menu"
      >
        <HelpCircle className="h-6 w-6" />
      </Button>

      {/* Help Sidebar */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Sidebar */}
          <div className="fixed right-0 top-0 bottom-0 w-80 bg-background border-l shadow-xl z-50 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-lg">Help & Tutorials</h2>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                aria-label="Close help menu"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                {/* Quick Start */}
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Quick Start
                  </h3>
                  <div className="space-y-1">
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => {
                        setIsOpen(false);
                        startTour('first-time-user');
                      }}
                    >
                      <PlayCircle className="h-4 w-4 mr-2" />
                      Take a Tour
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => {
                        setIsOpen(false);
                        setHelpCenterOpen(true);
                      }}
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      View All Guides
                    </Button>
                  </div>
                </div>

                <Separator />

                {/* Quick Links */}
                <div>
                  <h3 className="text-sm font-semibold mb-2">Quick Links</h3>
                  <div className="space-y-1">
                    {quickLinks.map((link) => {
                      const Icon = link.icon;
                      return (
                        <Button
                          key={link.label}
                          variant="ghost"
                          className="w-full justify-start"
                          onClick={() => {
                            setIsOpen(false);
                            link.onClick();
                          }}
                        >
                          <Icon className="h-4 w-4 mr-2" />
                          {link.label}
                          <ChevronRight className="h-4 w-4 ml-auto" />
                        </Button>
                      );
                    })}
                  </div>
                </div>

                <Separator />

                {/* Interactive Tutorials */}
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <PlayCircle className="h-4 w-4" />
                    Interactive Tutorials
                  </h3>
                  <div className="space-y-1">
                    {allTours.map((tour) => {
                      // Get appropriate icon based on tour ID
                      let Icon = PlayCircle;
                      if (tour.id.includes('scenario')) {
                        Icon = BarChart3;
                      } else if (tour.id.includes('stochastic') || tour.id.includes('projection')) {
                        Icon = TrendingUp;
                      } else if (tour.id.includes('shock') || tour.id.includes('borrowing')) {
                        Icon = Settings;
                      } else if (tour.id === 'first-time-user') {
                        Icon = Zap;
                      }

                      return (
                        <Button
                          key={tour.id}
                          variant="outline"
                          className="w-full justify-start"
                          onClick={() => {
                            setIsOpen(false);
                            logger.info('Help: Starting tour', { tourId: tour.id });
                            startTour(tour.id as TourId);
                          }}
                        >
                          <Icon className="h-4 w-4 mr-2" />
                          {tour.title}
                          {hasCompletedTour(tour.id as TourId) && (
                            <span className="ml-auto text-xs text-muted-foreground">✓</span>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </ScrollArea>
          </div>
        </>
      )}

      <HelpCenter 
        open={helpCenterOpen} 
        onOpenChange={(open) => {
          setHelpCenterOpen(open);
          if (!open) {
            // Clear selected article when closing
            setSelectedArticleId(undefined);
          }
        }}
        initialArticleId={selectedArticleId}
      />
    </>
  );
}

